import { DocumentSubType } from "@/briefpoint-client";
import { getSubTypeText } from "@/lib/utils";
import { SectionCard } from "./SectionCard";
import styles from "./StepsSection.module.scss";

export function StepsSection() {
  return (
    <SectionCard
      id="steps"
      ariaLabelledby="steps-h"
      title="Step-by-Step"
      description="Clear instructions you can follow in order. Expand a topic to view the steps."
      className={styles.steps}
    >
      <details open>
        <summary>
          <span className={styles.chev}>▸</span> How to Respond to discovery
        </summary>
        <ol>
          <li>
            <strong>Upload Opposing Counsel's Discovery (.pdf)</strong>
            <ul>
              <li>
                Upload the <em>.pdf</em> of the requests/interrogatories.
              </li>
            </ul>
          </li>
          <li>
            <strong>Confirm Case Info &amp; Venue</strong>
            <ul>
              <li>Verify your case details and venue.</li>
              <li>
                If venue is incorrect, click the <em>Feedback</em> tab on the
                right and report it.
              </li>
            </ul>
          </li>
          <li>
            <strong>Confirm Parties</strong>
            <ul>
              <li>Ensure party roles are correct.</li>
              <li>
                Use <em>Refer to Responding Party As</em> to define your
                client's name for the document.
              </li>
            </ul>
          </li>
          <li>
            <strong>Object</strong>
            <ul>
              <li>
                Either manually select objections from the left menu, or click{" "}
                <em>Suggest</em> for Briefpoint to propose objections.
              </li>
              <li>
                Use the star icons on each objection to toggle between the{" "}
                <em>default</em> and <em>Briefpoint's bespoke</em> versions.
              </li>
              <li>All default content is customizable from the left menu.</li>
            </ul>
          </li>
          <li>
            <strong>Add Responses (or Send to Client)</strong>
            <ul>
              <li>
                Enter your responses directly, or click <em>Send to Client</em>{" "}
                to collect answers.
              </li>
            </ul>
          </li>
          <li>
            <strong>Export to Word</strong>
            <ul>
              <li>
                Click <em>Open in Word</em> to generate the finalized Word
                document response.
              </li>
            </ul>
          </li>
        </ol>
      </details>

      <details>
        <summary>
          <span className={styles.chev}>▸</span> Propounding Discovery
        </summary>
        <ol>
          <li>
            <strong>
              Upload {getSubTypeText(DocumentSubType.Complaint, "").singular}{" "}
              .pdf
            </strong>
            <ul>
              <li>
                Click <em>Propound Discovery</em> →{" "}
                <em>Upload Pleading .pdf</em>
              </li>
              <li>Upload a PDF of your complaint</li>
              <li>
                To use only templates (no AI-generated content), see "Propound
                Using Templates Only".
              </li>
            </ul>
          </li>
          <li>
            <strong>Select Position</strong>
            <ul>
              <li>
                Choose <em>Plaintiff</em> or <em>Defendant</em> (defense sets
                differ significantly).
              </li>
            </ul>
          </li>
          <li>
            <strong>Review Case Information</strong>
            <ul>
              <li>Verify extracted parties, facts, dates, and allegations.</li>
            </ul>
          </li>
          <li>
            <strong>Generate Discovery Documents</strong>
            <ul>
              <li>
                Select RFAs, RFPs, Interrogatories, then click <em>Confirm</em>.
              </li>
            </ul>
          </li>
          <li>
            <strong>Review Generated Requests</strong>
            <ul>
              <li>
                Briefpoint drafts tailored requests designed to avoid common
                objections, with specific facts and details.
              </li>
            </ul>
          </li>
          <li>
            <strong>Add Template Requests (Optional)</strong>
            <ul>
              <li>
                Click <em>Add from Library</em> →{" "}
                <em>Show Briefpoint Library</em>.
              </li>
              <li>Filter by practice area and request type; click to add.</li>
            </ul>
          </li>
          <li>
            <strong>Preview &amp; Download</strong>
            <ul>
              <li>
                Click <em>Open</em> to preview (intro, definitions, requests,
                certificate of service).
              </li>
              <li>Download the finalized document.</li>
            </ul>
          </li>
        </ol>
      </details>

      <details>
        <summary>
          <span className={styles.chev}>▸</span> Propound Using Templates Only
        </summary>
        <ol>
          <li>
            <strong>Start from Case Page</strong>
            <ul>
              <li>
                Click <em>the case title</em> or{" "}
                <em>go to the case tab to find your case</em>, then select{" "}
                <em>"Draft Request"</em>.
              </li>
            </ul>
          </li>
          <li>
            <strong>Add Content from Library</strong>
            <ul>
              <li>
                After confirming info, add content by selecting "Add from
                Library".
              </li>
            </ul>
          </li>
          <li>
            <strong>Open in Word</strong>
            <ul>
              <li>
                Click <em>Open in Word</em> to download your propoundign
                discovery set to Word.
              </li>
            </ul>
          </li>
          <li>
            <strong>Review / Edit Templates</strong>
            <ul>
              <li>
                Go to your <em>Library</em> → <em>Discovery Requests</em> to
                review or edit.
              </li>
            </ul>
          </li>
          <li>
            <strong>Preview Briefpoint Library</strong>
            <ul>
              <li>
                Click <em>Show Briefpoint Library</em> to preview 6,000+
                template requests.
              </li>
            </ul>
          </li>
        </ol>
      </details>

      <details>
        <summary>
          <span className={styles.chev}>▸</span> Client Responses — Send to
          Client
        </summary>
        <ol>
          <li>
            <strong>Upload Opposing Counsel's Request</strong>
            <ul>
              <li>
                Upload the PDF and confirm the extracted case information.
              </li>
            </ul>
          </li>
          <li>
            <strong>Select Requests to Send</strong>
            <ul>
              <li>
                Click <em>Send to Client</em> and choose which
                interrogatories/requests to include.
              </li>
            </ul>
          </li>
          <li>
            <strong>Enter Client Email</strong>
            <ul>
              <li>
                Input the client's email, then click <em>Confirm</em> to
                generate instructions.
              </li>
            </ul>
          </li>
          <li>
            <strong>Generate Access Code</strong>
            <ul>
              <li>
                Briefpoint creates a unique access code securing the client
                portal.
              </li>
            </ul>
          </li>
          <li>
            <strong>Send Email to Client</strong>
            <ul>
              <li>
                Click <em>Open Email</em> to launch your email app and paste the
                instructions.
              </li>
            </ul>
          </li>
          <li>
            <strong>
              NOTICE: Briefpoint does NOT send emails to your client.
            </strong>
            <ul>
              <li>
                Use <em>Open Email</em> or <em>Copy Email</em> to send
                instructions from your email.
              </li>
            </ul>
          </li>
          <li>
            <strong>One Code per Client</strong>
            <ul>
              <li>
                All discovery you send your client is tied to their link and
                code.
              </li>
              <li>
                When you click "Confirm" on your client's information, that
                discovery set is added to the same link/code.
              </li>
              <li>
                Best practice: send your client the link and code after you've
                confirmed all sets you intend to include.
              </li>
            </ul>
          </li>
        </ol>
      </details>

      <details>
        <summary>
          <span className={styles.chev}>▸</span> How Clients Respond
        </summary>
        <ol>
          <li>
            <strong>Client Access</strong>
            <ul>
              <li>
                Client clicks your link, enters the access code, and clicks{" "}
                <em>Respond</em>.
              </li>
              <li>You can view the same portal using the same code.</li>
            </ul>
          </li>
          <li>
            <strong>Response Interface</strong>
            <ul>
              <li>Each request appears with a plain-English explanation.</li>
              <li>
                Client types responses; prior answers appear on the right.
              </li>
            </ul>
          </li>
          <li>
            <strong>Review &amp; Submission</strong>
            <ul>
              <li>
                Review page highlights incomplete items; clients can save or
                submit.
              </li>
              <li>
                If edits are needed after submission, open the document in the
                Document List, expand <em>Client Activity</em>, and select{" "}
                <em>Re-open</em>.
              </li>
            </ul>
          </li>
          <li>
            <strong>Notification &amp; Tracking</strong>
            <ul>
              <li>
                You get an email on submission; a green badge appears in Client
                Activity.
              </li>
              <li>
                Yellow badges indicate no open/view or pending responses; use
                the ••• menu to send reminders.
              </li>
            </ul>
          </li>
          <li>
            <strong>Incorporate Responses</strong>
            <ul>
              <li>
                Click <em>Copy and Add</em> to insert client text beneath your
                objections (formatting customizable).
              </li>
            </ul>
          </li>
          <li>
            <strong>Generate the Final Response</strong>
            <ul>
              <li>
                Click <em>Open Word</em> for the completed document (caption,
                prelims, general objections, and all requests with responses).
              </li>
            </ul>
          </li>
        </ol>
      </details>

      <details>
        <summary>
          <span className={styles.chev}>▸</span> Objections — Customize,
          Suggest, and Order
        </summary>
        <ol>
          <li>
            <strong>Add Standard Objections</strong>
            <ul>
              <li>Click any objection name to add it to the response.</li>
            </ul>
          </li>
          <li>
            <strong>View Variations</strong>
            <ul>
              <li>Use the dropdown to select from different versions.</li>
            </ul>
          </li>
          <li>
            <strong>Edit from Left Menu (Saves to Library)</strong>
            <ul>
              <li>
                Click the pencil next to an objection in the left menu, edit
                wording, and <em>Save</em> for reuse.
              </li>
            </ul>
          </li>
          <li>
            <strong>Edit Under a Specific Request (One-off)</strong>
            <ul>
              <li>
                Use the pencil under the request/interrogatory to adjust just
                that occurrence.
              </li>
            </ul>
          </li>
          <li>
            <strong>AI-Suggested Objections</strong>
            <ul>
              <li>
                Click <em>Suggest</em> for tailored selections with written
                rationales (ambiguities explicitly flagged).
              </li>
            </ul>
          </li>
          <li>
            <strong>Order &amp; Toggle</strong>
            <ul>
              <li>
                Drag to reorder; toggle between manual and Bespoke sets via the
                three-star control.
              </li>
            </ul>
          </li>
        </ol>
      </details>
    </SectionCard>
  );
}
